import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import {
  heroData as defaultHeroData,
  servicesData as defaultServicesData,
  activitiesData as defaultActivitiesData,
  gamesData as defaultGamesData,
  contactData as defaultContactData,
  headerData as defaultHeaderData,
  footerData as defaultFooterData,
  images as defaultImages,
  portfolioData as defaultPortfolioData,
  trackingConfig as defaultTrackingConfig,
  PortfolioItem,
} from '../data';

const STORAGE_KEY = 'cedevium-admin-data';
const DEFAULT_PASSWORD_HASH = 'fb5cdf69f87487522d3ea9ccc4d9b3c09e2c7027715f446207f5bf2fa887f2fc';

async function hashPassword(password: string): Promise<string> {
  const data = new TextEncoder().encode(password);
  const buf = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

// ── Types ──────────────────────────────────────────────────────────────────
export type HeroData = typeof defaultHeroData;
export type ServicesData = typeof defaultServicesData;
export type ActivitiesData = typeof defaultActivitiesData;
export type GamesData = typeof defaultGamesData;
export type ContactData = typeof defaultContactData;
export type HeaderData = typeof defaultHeaderData;
export type FooterData = typeof defaultFooterData;
export type ImagesData = typeof defaultImages;
export type PortfolioData = typeof defaultPortfolioData;
export type TrackingConfig = typeof defaultTrackingConfig;
export type { PortfolioItem };

export interface CustomPage {
  id: string;
  slug: string;
  title: string;
  content: string;
  published: boolean;
  createdAt: string;
}

export interface EmailConfig {
  enabled: boolean;
  serviceId: string;
  templateId: string;
  confirmationTemplateId: string;
  publicKey: string;
  recipientEmail: string;
}

export interface LogoConfig {
  type: 'text' | 'image';
  imageData: string;
  imageAlt: string;
  favicon: string;
  appleTouchIcon: string;
}

// ── Default values ──────────────────────────────────────────────────────────

const defaultPortfolioConfig: PortfolioData = defaultPortfolioData;
const defaultTracking: TrackingConfig = defaultTrackingConfig;

const defaultEmailConfig: EmailConfig = {
  enabled: false,
  serviceId: '',
  templateId: '',
  confirmationTemplateId: '',
  publicKey: '',
  recipientEmail: '',
};

const defaultLogoConfig: LogoConfig = {
  type: 'text',
  imageData: '',
  imageAlt: 'Cedevium Services',
  favicon: '',
  appleTouchIcon: '',
};

interface AdminState {
  heroData: HeroData;
  servicesData: ServicesData;
  activitiesData: ActivitiesData;
  gamesData: GamesData;
  contactData: ContactData;
  headerData: HeaderData;
  footerData: FooterData;
  images: ImagesData;
  customPages: CustomPage[];
  emailConfig: EmailConfig;
  logoConfig: LogoConfig;
  portfolioData: PortfolioData;
  trackingConfig: TrackingConfig;
  passwordHash: string;
}

interface AdminDataContextType extends AdminState {
  isAdminOpen: boolean;
  isAuthenticated: boolean;
  remoteLoaded: boolean;
  prefilledSubject: string;
  openAdmin: () => void;
  closeAdmin: () => void;
  login: (password: string) => Promise<boolean>;
  logout: () => void;
  changePassword: (currentPwd: string, newPwd: string) => Promise<boolean>;
  updateHeroData: (data: HeroData) => void;
  updateServicesData: (data: ServicesData) => void;
  updateActivitiesData: (data: ActivitiesData) => void;
  updateGamesData: (data: GamesData) => void;
  updateContactData: (data: ContactData) => void;
  updateHeaderData: (data: HeaderData) => void;
  updateFooterData: (data: FooterData) => void;
  updateImages: (data: ImagesData) => void;
  updateEmailConfig: (config: EmailConfig) => void;
  updateLogoConfig: (config: LogoConfig) => void;
  updatePortfolioData: (data: PortfolioData) => void;
  updateTrackingConfig: (config: TrackingConfig) => void;
  setPrefilledSubject: (subject: string) => void;
  addPage: (page: Omit<CustomPage, 'id' | 'createdAt'>) => void;
  updatePage: (id: string, page: Partial<CustomPage>) => void;
  deletePage: (id: string) => void;
  resetToDefaults: () => void;
}

// ── Default state ───────────────────────────────────────────────────────────
const defaultAdminState: AdminState = {
  heroData: defaultHeroData,
  servicesData: defaultServicesData,
  activitiesData: defaultActivitiesData,
  gamesData: defaultGamesData,
  contactData: defaultContactData,
  headerData: defaultHeaderData,
  footerData: defaultFooterData,
  images: defaultImages,
  customPages: [],
  emailConfig: defaultEmailConfig,
  logoConfig: defaultLogoConfig,
  portfolioData: defaultPortfolioConfig,
  trackingConfig: defaultTracking,
  passwordHash: DEFAULT_PASSWORD_HASH,
};

function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

function migrateActivities(stored: any) {
  if (!stored?.activitiesData?.activities) return stored?.activitiesData;
  return {
    ...defaultActivitiesData,
    ...stored.activitiesData,
    activities: stored.activitiesData.activities.map((a: any) => ({
      image: '',
      showIcon: true,
      contactSubject: 'autre',
      ...a,
    })),
  };
}

function mergeState(base: AdminState, parsed: any): AdminState {
  return {
    ...deepClone(base),
    ...parsed,
    activitiesData: migrateActivities(parsed) || deepClone(defaultActivitiesData),
    emailConfig: { ...defaultEmailConfig, ...(parsed.emailConfig || {}) },
    logoConfig: { ...defaultLogoConfig, ...(parsed.logoConfig || {}) },
    portfolioData: { ...defaultPortfolioConfig, ...(parsed.portfolioData || {}) },
    trackingConfig: {
      ...defaultTracking,
      ...(parsed.trackingConfig || {}),
      matomoSiteId: parsed.trackingConfig?.matomoSiteId || defaultTracking.matomoSiteId,
    },
    passwordHash: parsed.passwordHash || DEFAULT_PASSWORD_HASH,
  };
}

function loadFromStorage(): AdminState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return mergeState(defaultAdminState, JSON.parse(stored));
  } catch {}
  return deepClone(defaultAdminState);
}

// ── JSONBin.io remote sync ──────────────────────────────────────────────────
const JSONBIN_BIN_ID = '6a343dd0da38895dfed92afa';
const JSONBIN_API_KEY = '$2a$10$KTLyt2mS6ZlRvJDTdaGbU.ENIN3UwKAWWbks2SfI.wz0XqiHxA34u';

function stripBase64(str: string): string {
  return str?.startsWith('data:') ? '' : (str ?? '');
}

function stripBase64ForSync(state: AdminState): AdminState {
  return {
    ...state,
    portfolioData: {
      ...state.portfolioData,
      items: state.portfolioData.items.map(item => ({
        ...item,
        image: stripBase64(item.image),
      })),
    },
    logoConfig: {
      ...state.logoConfig,
      imageData: stripBase64(state.logoConfig.imageData),
      favicon: stripBase64(state.logoConfig.favicon),
      appleTouchIcon: stripBase64(state.logoConfig.appleTouchIcon),
    },
    activitiesData: {
      ...state.activitiesData,
      activities: (state.activitiesData.activities as any[]).map(a => ({
        ...a,
        image: stripBase64(a.image ?? ''),
      })),
    },
  };
}

export async function loadFromJsonBin(): Promise<AdminState | null> {
  try {
    const res = await fetch(
      `https://api.jsonbin.io/v3/b/${JSONBIN_BIN_ID}/latest`,
      { headers: { 'X-Master-Key': JSONBIN_API_KEY } }
    );
    if (!res.ok) return null;
    const { record } = await res.json();
    return mergeState(defaultAdminState, record);
  } catch {
    return null;
  }
}

async function saveToJsonBin(state: AdminState): Promise<void> {
  try {
    await fetch(`https://api.jsonbin.io/v3/b/${JSONBIN_BIN_ID}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Master-Key': JSONBIN_API_KEY,
      },
      body: JSON.stringify(stripBase64ForSync(state)),
    });
  } catch {}
}

export async function loadConfigJson(): Promise<AdminState | null> {
  try {
    const res = await fetch('./config.json?v=' + Date.now());
    if (!res.ok) return null;
    const parsed = await res.json();
    return mergeState(defaultAdminState, parsed);
  } catch {
    return null;
  }
}

const AdminDataContext = createContext<AdminDataContextType | null>(null);

// ── Provider ────────────────────────────────────────────────────────────────
export function AdminDataProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<AdminState>(loadFromStorage);
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => sessionStorage.getItem('cedevium-admin-auth') === 'true'
  );
  const [prefilledSubject, setPrefilledSubject] = useState('');
  const [remoteLoaded, setRemoteLoaded] = useState(false);
  const isMounted = useRef(false);

  // Au démarrage :
  // - Admin : pousse son localStorage vers JSONBin.io (source de vérité à jour)
  // - Visiteur : charge toujours depuis JSONBin.io (ignore le localStorage en cache)
  useEffect(() => {
    const isAdmin = sessionStorage.getItem('cedevium-admin-auth') === 'true';
    if (isAdmin) {
      saveToJsonBin(data);
      setRemoteLoaded(true);
    } else {
      loadFromJsonBin().then(remote => {
        if (remote) setData(remote);
        setRemoteLoaded(true);
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {}
  }, [data]);

  // Sauvegarde vers JSONBin.io (admin uniquement, avec debounce 2s après chaque modif)
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    if (!isAuthenticated) return;
    const timer = setTimeout(() => { saveToJsonBin(data); }, 2000);
    return () => clearTimeout(timer);
  }, [data, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Remove legacy plaintext password from localStorage
  useEffect(() => { localStorage.removeItem('cedevium-admin-password'); }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        setIsAdminOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  function update<K extends keyof AdminState>(key: K, value: AdminState[K]) {
    setData(prev => ({ ...prev, [key]: value }));
  }

  const login = async (password: string): Promise<boolean> => {
    const hash = await hashPassword(password);
    if (hash === data.passwordHash) {
      setIsAuthenticated(true);
      sessionStorage.setItem('cedevium-admin-auth', 'true');
      return true;
    }
    return false;
  };

  const logout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('cedevium-admin-auth');
    setIsAdminOpen(false);
  };

  const changePassword = async (currentPwd: string, newPwd: string): Promise<boolean> => {
    const currentHash = await hashPassword(currentPwd);
    if (currentHash !== data.passwordHash) return false;
    const newHash = await hashPassword(newPwd);
    setData(prev => ({ ...prev, passwordHash: newHash }));
    return true;
  };

  const addPage = (page: Omit<CustomPage, 'id' | 'createdAt'>) => {
    const newPage: CustomPage = {
      ...page,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
    };
    setData(prev => ({ ...prev, customPages: [...prev.customPages, newPage] }));
  };

  const updatePage = (id: string, updates: Partial<CustomPage>) => {
    setData(prev => ({
      ...prev,
      customPages: prev.customPages.map(p => p.id === id ? { ...p, ...updates } : p),
    }));
  };

  const deletePage = (id: string) => {
    setData(prev => ({ ...prev, customPages: prev.customPages.filter(p => p.id !== id) }));
  };

  return (
    <AdminDataContext.Provider value={{
      ...data,
      isAdminOpen,
      isAuthenticated,
      remoteLoaded,
      prefilledSubject,
      openAdmin: () => setIsAdminOpen(true),
      closeAdmin: () => setIsAdminOpen(false),
      login,
      logout,
      changePassword,
      updateHeroData: (v) => update('heroData', v),
      updateServicesData: (v) => update('servicesData', v),
      updateActivitiesData: (v) => update('activitiesData', v),
      updateGamesData: (v) => update('gamesData', v),
      updateContactData: (v) => update('contactData', v),
      updateHeaderData: (v) => update('headerData', v),
      updateFooterData: (v) => update('footerData', v),
      updateImages: (v) => update('images', v),
      updateEmailConfig: (v) => update('emailConfig', v),
      updateLogoConfig: (v) => update('logoConfig', v),
      updatePortfolioData: (v) => update('portfolioData', v),
      updateTrackingConfig: (v) => update('trackingConfig', v),
      setPrefilledSubject,
      addPage,
      updatePage,
      deletePage,
      resetToDefaults: () => {
        setData(deepClone(defaultAdminState));
        localStorage.removeItem(STORAGE_KEY);
      },
    }}>
      {children}
    </AdminDataContext.Provider>
  );
}

export function useAdminData(): AdminDataContextType {
  const ctx = useContext(AdminDataContext);
  if (!ctx) throw new Error('useAdminData must be used within AdminDataProvider');
  return ctx;
}
